import { cn } from '@/lib/utils'
import { Download } from 'lucide-react'
import React from 'react'
import { Button } from '@/components/ui/button'

interface FileViewerProps {
  url: string
  className?: string
  onFileClick?: () => void
}

export const FileViewer: React.FC<FileViewerProps> = ({ url, className, onFileClick }) => {
  const name = url.split('/').pop()?.split('.').shift() || ''
  const type = url.split('.').pop()?.toLowerCase() || ''
  const isImage = /\.(jpe?g|png|gif|webp|svg)$/i.test(url)
  const isPdf = /\.pdf(\?.*)?$/i.test(url)
  const isAudio = /\.(mp3|wav|ogg)$/i.test(url)
  const isVideo = /\.(mp4|webm|mov)$/i.test(url)
  const isOffice = /\.(docx?|xlsx?|pptx?)$/i.test(url)

  if (isImage) {
    return <img src={url} alt={name} onClick={onFileClick} className={cn("min-w-[70vh] h-auto min-h-auto max-h-auto rounded-2xl shadow-lg", className)} />
  }

  if (isPdf) {
    return <iframe onClick={onFileClick} src={url} title={name} className={cn('w-full h-[80vh] border rounded', className)} />
  }

  if (isAudio) {
    return (
      <div className={cn("p-2 border rounded w-full max-w-2xl bg-gray-50", className)} onClick={onFileClick}>
        <p className="mb-2 text-sm font-medium">{name}</p>
        <audio controls className="w-full" autoPlay>
          <source src={url} type={`audio/${type}`} />
          Seu navegador não suporta áudio.
        </audio>
      </div>
    )
  }

  if (isVideo) {
    return (
      <div className={cn("w-full max-w-4xl", className)} onClick={onFileClick}>
        <video controls className="w-full rounded shadow-lg">
          <source src={url} type={`video/${type}`} />
          Seu navegador não suporta vídeo.
        </video>
      </div>
    )
  }

  if (isOffice) {
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
    return <iframe onClick={onFileClick} src={officeViewerUrl} title={name} className="w-full h-[80vh] border rounded" />
  }

  return (
    <>
      {!url?.length ? (
        <div className="p-4 border rounded bg-gray-100 flex flex-col gap-2 items-center" onClick={onFileClick}>
          <p className="text-gray-800">Arquivo não encontrado.</p>
        </div>
      ) : (
        <div className="p-4 border rounded bg-gray-100 flex flex-col gap-2 items-center" onClick={onFileClick}>
          <p className="text-gray-800">Pré-visualização não suportada para este tipo de arquivo.</p>
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline justify-center w-full">
            <Button className="w-full flex items-center rounded-2xl p-2 bg-secondary/30 hover:bg-secondary/60 dark:text-white text-primary text-sm font-medium">
              Baixar {name}
              <Download className="h-4 w-4 ml-1" />
            </Button>
          </a>
        </div>
      )}
    </>
  )
}
